import { invalidateCachePattern } from "../lib/cache";

import { supabase } from "./supabase";

export interface UserHomePreference {
  sectionId: string;
  isVisible: boolean;
  displayOrder?: number;
}

export interface UserPlaylist {
  id: string;
  userId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  itemCount?: number;
}

export interface PlaylistItem {
  id: string;
  playlistId: string;
  itemType: "station" | "podcast";
  itemId: string;
  position: number;
  addedAt: string;
  // Expanded data (joined)
  name?: string;
  imageUrl?: string;
}

/**
 * Fetches user preferences for home screen sections.
 */
export const getUserHomePreferences = async (
  userId: string,
): Promise<UserHomePreference[]> => {
  const { data, error } = await supabase
    .from("user_home_preferences")
    .select("*")
    .eq("user_id", userId);

  if (error) {
    console.error("Error fetching user preferences:", error);
    return [];
  }

  return data.map((row) => ({
    sectionId: row.section_id as string,
    isVisible: row.is_visible as boolean,
    displayOrder: row.display_order as number,
  }));
};

/**
 * Updates a user's preference for a specific home section.
 */
export const updateUserHomePreference = async (
  userId: string,
  sectionId: string,
  isVisible: boolean,
) => {
  // console.log('[userLibrary] Updating preference:', { userId, sectionId, isVisible });
  if (!userId || !sectionId) {
    console.warn(
      "[userLibrary] Missing userId or sectionId for preference update",
    );
    return;
  }

  const { error } = await supabase.from("user_home_preferences").upsert({
    user_id: userId,
    section_id: sectionId,
    is_visible: isVisible,
  });

  if (error) throw error;

  // Invalidate local cache for home data to force refresh on next visit
  // Clears both memory and localStorage using the new cache system
  try {
    // Clear all home data variants (all countries)
    invalidateCachePattern(/:home:/);

    // Also clear legacy cache just in case
    Object.keys(localStorage).forEach((key) => {
      if (key.includes("home_data")) {
        localStorage.removeItem(key);
      }
    });

    // console.log('[userLibrary] Cache invalidated for home data');
  } catch (e) {
    console.warn("Failed to clear home data cache", e);
  }
};

/**
 * Bulk updates user preferences for home sections.
 * More efficient than calling single update multiple times.
 */
export const updateUserHomePreferences = async (
  userId: string,
  preferences: { sectionId: string; isVisible: boolean }[],
) => {
  if (!userId || preferences.length === 0) return;

  const updates = preferences.map((p) => ({
    user_id: userId,
    section_id: p.sectionId,
    is_visible: p.isVisible,
  }));

  const { error } = await supabase
    .from("user_home_preferences")
    .upsert(updates);

  if (error) throw error;

  // Invalidate local cache efficiently
  try {
    invalidateCachePattern(/:home:/);

    // Clear legacy cache
    Object.keys(localStorage).forEach((key) => {
      if (key.includes("home_data")) {
        localStorage.removeItem(key);
      }
    });
  } catch (e) {
    console.warn("Failed to clear home data cache", e);
  }
};

/**
 * Creates a new custom playlist.
 */
export const createPlaylist = async (
  userId: string,
  title: string,
): Promise<UserPlaylist | null> => {
  const { data, error } = await supabase
    .from("user_playlists")
    .insert({ user_id: userId, title })
    .select()
    .single();

  if (error) {
    console.error("Error creating playlist:", error);
    return null;
  }

  return {
    id: data.id,
    userId: data.user_id,
    title: data.title,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
};

/**
 * Fetches all playlists for a user.
 */
export const getUserPlaylists = async (
  userId: string,
): Promise<UserPlaylist[]> => {
  const { data, error } = await supabase
    .from("user_playlists")
    .select("*, playlist_items(count)") // Initial count check
    .eq("user_id", userId)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching playlists:", error);
    return [];
  }

  return data.map((row) => ({
    id: row.id as string,
    userId: row.user_id as string,
    title: row.title as string,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
    itemCount:
      (
        (row as Record<string, unknown>).playlist_items as
          | Array<{ count: number }>
          | undefined
      )?.[0]?.count || 0,
  }));
};

/**
 * Adds an item (station/podcast) to a playlist.
 */
export const addToPlaylist = async (
  playlistId: string,
  type: "station" | "podcast",
  itemId: string,
) => {
  // Get current max position
  const { data: maxPosData } = await supabase
    .from("playlist_items")
    .select("position")
    .eq("playlist_id", playlistId)
    .order("position", { ascending: false })
    .limit(1)
    .single();

  const nextPos = (maxPosData?.position || 0) + 1;

  const { error } = await supabase.from("playlist_items").insert({
    playlist_id: playlistId,
    item_type: type,
    item_id: itemId,
    position: nextPos,
  });

  if (error) throw error;
};

/**
 * Removes an item from a playlist.
 */
export const removeFromPlaylist = async (
  playlistId: string,
  type: "station" | "podcast",
  itemId: string,
) => {
  const { error } = await supabase
    .from("playlist_items")
    .delete()
    .match({ playlist_id: playlistId, item_type: type, item_id: itemId });

  if (error) throw error;
};

/**
 * Fetches items in a playlist.
 * @todo Implement enrichment (joining with station/podcast data)
 */
export const getPlaylistItems = async (
  playlistId: string,
): Promise<PlaylistItem[]> => {
  const { data, error } = await supabase
    .from("playlist_items")
    .select("*")
    .eq("playlist_id", playlistId)
    .order("position", { ascending: true });

  if (error) {
    console.error("Error fetching playlist items:", error);
    return [];
  }

  return data.map((row) => ({
    id: row.id as string,
    playlistId: row.playlist_id as string,
    itemType: row.item_type as "station" | "podcast",
    itemId: row.item_id as string,
    position: row.position as number,
    addedAt: row.added_at as string,
  }));
};
